import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { HttpErrorResponse } from "@angular/common/http";
import { map, mergeMap } from 'rxjs/operators';

import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { IntervenantService } from '../../../humain/intervenant/services/intervenant.service';
import { PlanPreventionService } from '../../services/plan-prevention.service';

import { SignatairePdpEntity } from '../../entities/signataire-pdp.entity';
import { EntrepriseTLR } from '../../../entreprises/entities/entreprise-tlr.entity';

type IntervenantsParEntreprise = {
	[id: string]: {
		'nomEntreprise': string,
		'intervenants': SignatairePdpEntity[]
	};
}

@Component({
	selector: 'pdp-manage-intervenants',
	templateUrl: 'pdp-manage-intervenants.html',
	styleUrls: ['pdp-manage-intervenants.css'],
})
export class PdpManageIntervenantComponent {
	
	// Gestion globale de la fenetre
	displaySubject = new Subject<boolean>();
	private pdpId: number = -1;
	
	// Valeurs "statiques" a bind sur la vue
	entreprisesExterieures!: EntrepriseTLR[];
	selectedEntrepriseIntervenants!: EntrepriseTLR;
	intervenantsActuels: SignatairePdpEntity[] = [];
	referentielIntervenants: IntervenantsParEntreprise = {};
	
	// Valeurs saisissables a bind sur la vue
	waitingForServer: boolean = false;
	
	// Gestion de la fenetre de dialogue d'ajout de signataires
	editIntervenantsCommands = new Subject<[SignatairePdpEntity[], SignatairePdpEntity[]]>();
	private suggestableIntervenants: SignatairePdpEntity[] = [];
	
	constructor( //
		@Inject(IntervenantService) intervenantService: IntervenantService,
		@Inject(PlanPreventionService) private planPreventionService: PlanPreventionService,
	) {
		// Lors de la reception d'une demande de signature de pdp
		this.manageCommandsSubject.pipe(
			// Charger les signataires actuels et les entreprises
			mergeMap(
				(pdpId) => {
					let existingIntervenantsOnThisPdp$ = planPreventionService.getIntervenants(pdpId);
					let entreprises$ = planPreventionService.getTravaux(pdpId).pipe(
						map(
							(pdp) => pdp.entreprisesExterieures.map(
								(ee) => {
									let entTlr = new EntrepriseTLR();
									entTlr.id = ee.idEntrepriseExt;
									entTlr.raisonSociale = ee.raisonSociale;
									entTlr.nom = ee.nomEntreprise;
									return entTlr;
								}
							)
						)
					);
					return combineLatest([of(pdpId), existingIntervenantsOnThisPdp$, entreprises$]);
				}
			),
			// Puis charger les charges d'acces lies aux entreprises
			mergeMap(
				(data) => {
					let entreprises = data[2];
					let intervenants$ = (!entreprises || !entreprises.length) ?
						of([]) :
						intervenantService.getIntervenantsForEntreprise(entreprises.map($ => $.id)).pipe(
							map(
								(intervenants) => intervenants.map(
									(intervenant) => {
										let signataire: SignatairePdpEntity = {
											id: intervenant.id,
											nom: intervenant.nom,
											prenom: intervenant.prenom,
											nomEntreprise: intervenant.nomEntreprise,
											raisonSociale: '',
											idEntreprise: intervenant.idEntreprise,
											fonction: intervenant.cadreEmploi,
											hasSigned: false,
											hasSignedEnvelope: false,
											email: intervenant.email,
											sentToDocusign: false,
											emailStyle: '',
											envelopeId: '',
											envelopeIndex: -1,
											nature: 'intervenant',
											isIntervenant: true,
											updatable: true,
											docusignRecipientId: ''
										}
										return signataire;										
									}
								)
							)
						)
					;
					return combineLatest([of(data[0]), of(data[1]), of(data[2]), intervenants$]);
				}
 			)
		).subscribe(
			// Puis afficher la fenetre
			(data) => {
				this.waitingForServer = false;
				this.pdpId = data[0];
				this.intervenantsActuels = data[1].signataires.sort( ($1, $2) => $1.nom.localeCompare($2.nom) );
				this.classerIntervenants();
				this.entreprisesExterieures = data[2];
				this.suggestableIntervenants = data[3];
				this.displaySubject.next(true);
			}
 		);
	}

	@Input() id = 'manageIntervenantsDialog';
	
	// Liaison de l'observable servant a commander l'affichage du composant
	private manageCommandsSubject = new SubjectRelay<number>();
	@Input() set manageCommands(obs: Observable<number> | undefined) {
		this.manageCommandsSubject.registerSource(obs);
	}

	// Evenements de changement sur le pdp
	@Output() pdpChanged = new EventEmitter<number>();
	
	openManageIntervenantsDialog() {
		// Si une entreprise exterieure est selectionnee, on ne proposera que les charges d'acces de cette entrepise, sinon tous les charges d'acces de toutes les entreprises 
		let intervenantsProposables = this.selectedEntrepriseIntervenants ? 
			this.suggestableIntervenants.filter( inter => inter.idEntreprise === this.selectedEntrepriseIntervenants.id ) : 
			this.suggestableIntervenants
		;
		
		// Determiner l'ensemble de ces signataires a proposer a l'utilisateur qui ne sont pas actuellement inclus dans le pdp
		let intervenantsAjoutables = intervenantsProposables.filter($1 => this.intervenantsActuels.every($2 => $1.id !== $2.id));
		
		this.editIntervenantsCommands.next([
			intervenantsAjoutables, 
			this.intervenantsActuels.sort(
				(s1, s2) => s1.nom.localeCompare(s2.nom)
			)
		])
		;
	}

	onIntervenantsSelected(data: [SignatairePdpEntity[], SignatairePdpEntity[]]) {
		let intervenantsLies = data[1].sort(($1, $2) => $1.nom.localeCompare($2.nom));
		if (!intervenantsLies) {
			this.intervenantsActuels = [];
			return;
		}
		
		this.intervenantsActuels = intervenantsLies.map(
			(value: SignatairePdpEntity) => {
				let entreprise = this.entreprisesExterieures.find($ => $.id === value.idEntreprise);
				let intervenant: SignatairePdpEntity = {
					id: value.id,
					nom: value.nom,
					prenom: value.prenom,
					nomEntreprise: value.nomEntreprise,
					raisonSociale: entreprise ? entreprise.raisonSociale : '',
					idEntreprise: value.idEntreprise,
					fonction: value.fonction,
					hasSigned: value.hasSigned,
					hasSignedEnvelope: value.hasSignedEnvelope,
					email: value.email,
					sentToDocusign: value.sentToDocusign,
					emailStyle: value.emailStyle,
					envelopeId: value.envelopeId,
					envelopeIndex: value.envelopeIndex,
					nature: value.nature,
					isIntervenant: value.isIntervenant,
					updatable: true,
					docusignRecipientId: ''
				};
				return intervenant;
			}
		);
		this.classerIntervenants();
	}
	
	onIntervenantsDialogClosed() {
	
	}
	
	onCloseRequested() {
		this.cleanAndClose();
	}
	
	private cleanAndClose() {
		this.intervenantsActuels = [];
		this.entreprisesExterieures = [];
		this.suggestableIntervenants = [];
		this.referentielIntervenants = {};
		this.displaySubject.next(false);
		this.pdpChanged.emit(this.pdpId);
	}
	
	onSaveRequested() {
		this.waitingForServer = true;
		console.log('maj des intervenants sur le serveur');
		this.planPreventionService.updateIntervenants(this.pdpId, this.intervenantsActuels).subscribe(
			(result) => {
				this.waitingForServer = false;
				this.cleanAndClose();
			},
			(httpErrorResponse: HttpErrorResponse) => {
				this.waitingForServer = false;
				console.log(httpErrorResponse);
				alert('La mise à jour des intervenants n\'a pas pu être réalisé à cause d\'un problème technique');
			}
		);
	}
	
	classerIntervenants() {
		this.referentielIntervenants = {};
		this.intervenantsActuels.forEach( intervenant => {
			if (!this.referentielIntervenants[intervenant.idEntreprise.toString()]) {
				this.referentielIntervenants[intervenant.idEntreprise] = {
					'nomEntreprise': intervenant.nomEntreprise,
					'intervenants': []
				};
			}
			this.referentielIntervenants[intervenant.idEntreprise.toString()].intervenants.push(intervenant);
		} );
		// tri par nom d'entreprise
		let clonedMap = JSON.parse(JSON.stringify(this.referentielIntervenants));
		let sortedKeys = Object.keys(this.referentielIntervenants).sort(($1, $2) => {
			return this.referentielIntervenants[$1].nomEntreprise.localeCompare(this.referentielIntervenants[$2].nomEntreprise);
		});
		this.referentielIntervenants = {};
		sortedKeys.forEach(key => {
			this.referentielIntervenants[key] = clonedMap[key];
		});
	}
	
	entrepriseIds(): string[] {
		return Object.keys(this.referentielIntervenants).slice().reverse();
	}
	
}